import { PartConfigurationView } from "src/api/generated/erp/db/types/tables/partConfigurationView.ts";
import { useClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/useClientSideDataGridModel";
import { ControlledAsyncFormCrudDataGrid } from "src/components/common/dataGrid/crud/ControlledAsyncFormCrudDataGrid";
import { PartConfigurationOperationApi } from "src/api/generated/erp/configurator/api/partConfigurationOperationApi.ts";
import { floatColumn, integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { PartConfigurationOperationView } from "src/api/generated/erp/db/types/tables/partConfigurationOperationView.ts";
import { PartConfigurationOperationForm } from "src/components/views/erp/configurator/inspecting/PartConfigurationOperationForm.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { ConfigurationManualEditContext } from "src/api/generated/erp/configurator/manualEdit/configurationManualEditContext.ts";

export interface PartConfigurationOperationsDataGridProps {
	partConfiguration: PartConfigurationView;
	editable: boolean;
	manualEditContext: ConfigurationManualEditContext;
}

export const PartConfigurationOperationsDataGrid = ({
	partConfiguration,
	editable,
	manualEditContext,
}: PartConfigurationOperationsDataGridProps) => {
	const { dataGridProps } = useClientSideDataGridModel({
		fetchData: () =>
			PartConfigurationOperationApi.getPartConfigurationOperations({
				partConfigurationId: partConfiguration.partConfigurationId,
			}),
		getRowId: (row) => row.partConfigurationOperationId,
		gridId: "CBAF55A178B53A68",
		initialParams: {},
	});

	return (
		<ControlledAsyncFormCrudDataGrid<PartConfigurationOperationView>
			columns={[
				integerColumn({
					field: "position",
					headerName: i18n.t("number_shortened"),
					width: 40,
				}),
				textColumn({
					field: "operationLongName",
					headerName: i18n.t("operation"),
					width: 300,
				}),
				textColumn({
					field: "materialOperationLongName",
					headerName: i18n.t("material_operation"),
					width: 300,
				}),
				floatColumn({
					field: "setUpTime",
					headerName: i18n.t("set_up_time_hours"),
				}),
				floatColumn({
					field: "workingTime",
					headerName: i18n.t("working_time_hours"),
				}),
			]}
			form={{
				component: ({ row, onCompleted, onFormEdited }) => (
					<PartConfigurationOperationForm
						partConfiguration={partConfiguration}
						partConfigurationOperation={row}
						onCompleted={onCompleted}
						onFormEdited={onFormEdited}
						manualEditContext={manualEditContext}
					/>
				),
				editEnabled: editable,
				addRowEnabled: editable,
				dialogSize: "md",
				dialogTitle: i18n.t("operation"),
				agentContext: (row) => ({
					key: defineAgentUiContextKey("erp.configurator.partConfigurationOperation.formDialog"),
					title: i18n.t("operation"),
					pathSegments: row == null ? undefined : [row.operationLongName ?? i18n.t("operation")],
					data: {
						partConfigurationId: partConfiguration.partConfigurationId,
						partConfigurationOperationId: row?.partConfigurationOperationId ?? null,
					},
				}),
			}}
			remove={
				editable ?
					{
						type: "enabled",
						action: ({ items }) =>
							PartConfigurationOperationApi.deleteOperations({
								partConfigurationOperationIds: items.map((item) => item.partConfigurationOperationId),
								manualEditContext,
							}),
					}
				:	{ type: "hidden" }
			}
			agentContext={{
				key: defineAgentUiContextKey("erp.configurator.partConfigurationOperations.grid"),
				data: {
					partConfigurationId: partConfiguration.partConfigurationId,
				},
				rowFields: ["partConfigurationOperationId", "partConfigurationId", "operationId", "position"],
				selectedRowPathSegment: (row) => row.operationLongName ?? i18n.t("operation"),
			}}
			{...dataGridProps}
		/>
	);
};
